import {
  useEstimateMeterReadings,
  useExcludeConsumersFromBilling,
  useMeterReadings,
} from "@features/bx/BulkBillGeneration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { AlertCircle, Calculator, FileText, XCircle } from "lucide-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { MissingReadingFilters } from "./Filters/MissingReadingFilters";

interface MeterReadingFiltersType {
  meterType: string;
  service: string;
  page?: number;
  limit?: number;
  veeStatus?: string;
}

// Type for tracking row actions
type RowActionType = "estimated" | "excluded" | null;

export const MissingReadings: React.FC = () => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // 🔥 NEW: Track which rows have been actioned
  const [rowActions, setRowActions] = useState<Record<string, RowActionType>>(
    {}
  );

  const pageSize = PAGE_SIZE;
  const { id, cycleId, action } = useParams();

  const [filters, setFilters] = useState<MeterReadingFiltersType>({
    meterType: "all",
    service: "all",
    page: currentPage,
    limit: pageSize,
    veeStatus: "MISSING",
  });

  const formatFiltersForHook = (
    filters: MeterReadingFiltersType
  ): { page: number; limit: number; search?: string; [key: string]: any } => {
    const hookFilters: {
      page: number;
      limit: number;
      search?: string;
      [key: string]: any;
    } = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    if (searchTerm.trim()) {
      hookFilters.search = searchTerm;
    }

    if (filters.meterType && filters.meterType !== "all") {
      hookFilters.meterType = filters.meterType;
    }

    if (filters.service && filters.service !== "all") {
      hookFilters.service = filters.service;
    }

    if (filters.veeStatus) {
      hookFilters.veeStatus = filters.veeStatus;
    }

    return hookFilters;
  };

  const { data: meterReadings, isLoading } = useMeterReadings(cycleId as string, id as string, formatFiltersForHook(filters));

  const { mutateAsync: estimateMeterReading, isPending: isEstimating } =
    useEstimateMeterReadings();

  const { mutateAsync: excludeMeterReading, isPending: isExcluding } =
    useExcludeConsumersFromBilling();

  // 🔥 NEW: Check if row can be estimated (not already actioned)
  const canEstimate = (rowId: string): boolean => {
    return !rowActions[rowId];
  };

  // 🔥 NEW: Check if row can be excluded (not already actioned)
  const canExclude = (rowId: string): boolean => {
    return !rowActions[rowId];
  };

  // 🔥 NEW: Get filtered selected rows based on action type
  const getActionableRows = (actionType: "estimate" | "exclude"): string[] => {
    return selectedRows.filter((rowId) => {
      if (actionType === "estimate") {
        return canEstimate(rowId);
      }
      return canExclude(rowId);
    });
  };

  const handleSelectionChange = (newSelection: string[]) => {
    setSelectedRows(newSelection);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 🔥 UPDATED: Handle Estimate All
  const handleEstimateAll = async () => {
    const actionableRows = getActionableRows("estimate");
    if (actionableRows.length === 0) return;

    // Extract meter_ids from row IDs
    const meterIds = actionableRows.map((rowId) => {
      const [meterId] = rowId.split("-");
      return meterId;
    });

    const payload = {
      cycle_id: cycleId,
      meter_ids: meterIds,
    };

    try {
      await estimateMeterReading(payload);

      // 🔥 Mark rows as estimated in local state
      const newRowActions = { ...rowActions };
      actionableRows.forEach((rowId) => {
        newRowActions[rowId] = "estimated";
      });
      setRowActions(newRowActions);

      // Clear selection
      setSelectedRows([]);
    } catch (error) {
      console.error("Failed to estimate readings:", error);
    }
  };

  // 🔥 UPDATED: Handle Exclude All
  const handleExcludeAll = async () => {
    const actionableRows = getActionableRows("exclude");
    console.log("Actionable Rows for Exclude:", actionableRows);
    if (actionableRows.length === 0) return;

    // Map meter_no -> consumer_number
    const consumerIds = actionableRows
      .map((meterNo) => {
        const reading = meterReadings?.results.find(
          (item) => item.meterNo === meterNo
        );
        return reading?.consumerNumber;
      })
      .filter(Boolean); // removes undefined if not found
    console.log("Consumer IDs to Exclude:", consumerIds);
    if (consumerIds.length === 0) return;

    const payload = {
      cycle_id: cycleId,
      consumer_ids: consumerIds,
      remote_utility_id: getRemoteUtilityId(),
      bill_draft_id: id,
    };

    try {
      await excludeMeterReading(payload);

      const newRowActions = { ...rowActions };
      actionableRows.forEach((rowId) => {
        newRowActions[rowId] = "excluded";
      });
      setRowActions(newRowActions);

      // Clear selection
      setSelectedRows([]);
    } catch (error) {
      console.error("Failed to exclude consumers:", error);
    }
  };

  // 🔥 UPDATED: Handle individual Estimate
  const handleEstimateRow = async (row: any) => {
    const rowId = row?.meterNumber;
    if (!canEstimate(rowId)) return;

    const payload = {
      cycle_id: cycleId,
      meter_ids: [row.meterId],
    };

    try {
      await estimateMeterReading(payload);

      // 🔥 Mark row as estimated
      setRowActions({
        ...rowActions,
        [rowId]: "estimated",
      });
    } catch (error) {
      console.error("Failed to estimate reading:", error);
    }
  };

  // 🔥 UPDATED: Handle individual Exclude
  const handleExcludeRow = async (row: any) => {
    const rowId = row?.meterNumber;
    if (!canExclude(rowId)) return;

    const payload = {
      cycle_id: cycleId,
      consumer_ids: [row.consumerId],
    };

    try {
      await excludeMeterReading(payload);

      // 🔥 Mark row as excluded
      setRowActions({
        ...rowActions,
        [rowId]: "excluded",
      });
    } catch (error) {
      console.error("Failed to exclude consumer:", error);
    }
  };

  // 🔥 NEW: Get action badge component
  const getActionBadge = (row: any) => {
    const rowId = row?.meterNumber;
    const actionType = rowActions[rowId];

    if (actionType === "estimated") {
      return (
        <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
          <Calculator className="h-3 w-3" />
          Estimated
        </Badge>
      );
    }

    if (actionType === "excluded") {
      return (
        <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Excluded
        </Badge>
      );
    }

    return null;
  };

  // 🔥 UPDATED: Define table columns with status column
  const columns: TableColumn<any>[] = [
    {
      key: "consumerNumber",
      header: "Consumer Number",
      type: "text",
      sortable: false,
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      type: "text",
      sortable: false,
    },
    {
      key: "meterNo",
      header: "Resource ID",
      type: "text",
      sortable: false,
    },
    {
      key: "meterType",
      header: "Resource Type",
      type: "text",
      sortable: false,
    },
    {
      key: "service",
      header: "Service",
      type: "text",
      sortable: false,
    },
    {
      key: "lastReading",
      header: "Last Reading",
      type: "text",
      sortable: false,
    },
    {
      key: "lastReadingDate",
      header: "Last Reading Date",
      type: "text",
      sortable: false,
    },
    // 🔥 NEW: Status column
    {
      key: "status",
      header: "Status",
      render: (value: any) => (
        <Badge className={getStatusBadgeClass(value)}>
          {value || "NA"}
        </Badge>
      )
    },
  ];

  const totalItems = meterReadings?.count || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handleFiltersChange = (newFilters: MeterReadingFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      meterType: "all",
      service: "all",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  // 🔥 NEW: Count actionable rows for bulk actions
  const estimableCount = getActionableRows("estimate").length;
  const excludableCount = getActionableRows("exclude").length;
  const [isEstimateModalOpen, setIsEstimateModalOpen] = useState(false);

  return (
    <div className="space-y-4">
      {/* Warning Banner */}
      <div className="flex items-start space-x-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <AlertCircle
          className="text-orange-600 mt-0.5 flex-shrink-0"
          size={20}
        />
        <p className="text-sm text-orange-800">
          These meters have no readings collected. You can estimate consumption
          or schedule a revisit for manual reading collection.
        </p>
      </div>

      {/* Table Header with Title and Bulk Action Buttons */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Missing Readings
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Meters with no reading collected in the current cycle
            </p>
          </div>

          <div className="mb-6">
            {/* Row 1: Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setIsEstimateModalOpen(true)}
                disabled={estimableCount === 0}
                className="flex items-center gap-2"
              >
                <Calculator className="h-4 w-4" />
                Estimate All {estimableCount > 0 && `(${estimableCount})`}
              </Button>

              <Button
                variant="destructive"
                onClick={handleExcludeAll}
                disabled={excludableCount === 0 || isExcluding}
                isLoading={isExcluding}
                className="flex items-center gap-2"
              >
                <XCircle className="h-4 w-4" />
                Exclude All {excludableCount > 0 && `(${excludableCount})`}
              </Button>
            </div>

            {/* Row 2: Info text */}
            {estimableCount === 0 && (
              <p className="mt-2 text-sm text-muted-foreground">
                Select Meter Reading to Estimate/Exclude
              </p>
            )}
          </div>
        </div>

        <GenericTable
          data={meterReadings?.results || []}
          columns={columns}
          rowKey="meterNo"
          selectable={true}
          selectedRows={selectedRows}
          onSelectionChange={handleSelectionChange}
          actionsType="buttons"
          onSearch={handleSearch}
          filterPanel={
            <MissingReadingFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleFiltersChange}
              onClearFilters={clearFilters}
            />
          }
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage: PAGE_SIZE,
            startItem,
            endItem,
          }}
          onPageChange={handlePageChange}
          emptyMessage="No meters with missing readings found"
          emptyIcon={<FileText className="h-8 w-8 text-gray-400" />}
        />
      </div>

      <Dialog open={isEstimateModalOpen} onOpenChange={setIsEstimateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Estimate Meter Readings</DialogTitle>
            <DialogDescription className="space-y-3 text-sm text-muted-foreground">
              <p>
                Readings will be estimated on the basis of set estimated rules
                in the read cycle.
              </p>
              <p>
                Use the refresh button on the top if you have validated readings
                and still data is visible.
              </p>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsEstimateModalOpen(false)}
            >
              Cancel
            </Button>

            <Button
              onClick={async () => {
                await handleEstimateAll();
                setIsEstimateModalOpen(false);
              }}
              isLoading={isEstimating}
            >
              Confirm & Estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
